/**
 * Home Serializers
 * Transform API data to match component expectations
 */

/**
 * Fix duplicate letters at the start of words in a string
 * Examples: "JJoo" -> "Joo", "CColonoumolo" -> "Colonoumolo", "LLe Gourmet Dakar" -> "Le Gourmet Dakar"
 * @param {string} text - Text to fix
 * @returns {string} - Fixed text
 */
function fixDuplicateFirstLetters(text) {
  if (!text || typeof text !== 'string') {
    return text;
  }

  // Fix duplicate first letters in each word
  // Pattern: matches a word character followed by the same character at word start
  const result = text.replace(/\b(\w)\1+/gi, '$1');
  console.log('[fixDuplicateFirstLetters] Input:', text, 'Output:', result);
  return result;
}

/**
 * Transform a single restaurant object from API format to component format
 * @param {Object} apiRestaurant - Restaurant object from API
 * @returns {Object} - Transformed restaurant object
 */
export function transformRestaurant(apiRestaurant) {
  if (!apiRestaurant || typeof apiRestaurant !== 'object') {
    return {};
  }

  // Clean the store name to fix duplicate first letters
  const cleanStoreName =
    fixDuplicateFirstLetters(apiRestaurant.storeName) || 'Restaurant';

  console.log(
    '[Serializer] Input storeName:',
    apiRestaurant.storeName,
    'Output storeName:',
    cleanStoreName
  );

  const isManualOpen =
    apiRestaurant.isOpen !== undefined ? apiRestaurant.isOpen !== false : true;
  const isWithinOpeningHours =
    apiRestaurant.isWithinOpeningHours !== undefined
      ? apiRestaurant.isWithinOpeningHours !== false
      : true;
  const calculatedIsOpen = isManualOpen && isWithinOpeningHours;

  return {
    // Preserve other fields first
    ...apiRestaurant,

    // Core fields - direct mapping
    id: apiRestaurant.id,
    storeName: cleanStoreName,
    logo: apiRestaurant.logo || null,
    images: [...(apiRestaurant.images || []), apiRestaurant.logo].filter(
      Boolean
    ),
    image:
      [...(apiRestaurant.images || []), apiRestaurant.logo].filter(
        Boolean
      )[0] || null,

    // Location
    lat: apiRestaurant.lat,
    lng: apiRestaurant.lng,
    address: apiRestaurant.address,
    city: apiRestaurant.city,

    // Delivery - with defaults
    deliveryFee: apiRestaurant.deliveryFee || 0,
    deliveryTime: apiRestaurant.deliveryTime || 30, // Default 30 min
    freeDelivery: apiRestaurant.freeDelivery || apiRestaurant.deliveryFee === 0,

    // Ratings and Stats - without fake defaults
    rating: apiRestaurant.statistics?.rating ?? apiRestaurant.rating ?? 0,
    ratingStars: Math.round(
      apiRestaurant.statistics?.rating ??
        apiRestaurant.ratingStars ??
        apiRestaurant.rating ??
        0
    ),
    reviewCount:
      apiRestaurant.statistics?.ratingCount ?? apiRestaurant.reviewCount ?? 0,
    views: apiRestaurant.statistics?.views ?? 0,
    favorites: apiRestaurant.statistics?.favorites ?? 0,
    ordersCount: apiRestaurant.statistics?.ordersCount ?? 0,

    // Categories - mapper depuis cuisine (array de strings) → garder le champ "categories"
    categories: normalizeCategories(
      apiRestaurant.categories || apiRestaurant.cuisine
    ),

    // Deals : mapper depuis hasPromotion + activePromotions
    hasDeal: !!(apiRestaurant.hasPromotion || apiRestaurant.hasDeal),
    discount: apiRestaurant.promotionSummary || apiRestaurant.discount || null,

    // Nouveaux champs disponibles (statut d'ouverture combiné)
    isOpen: calculatedIsOpen,
    isWithinOpeningHours: isWithinOpeningHours,
    slug: apiRestaurant.slug || null,
    openingHours: apiRestaurant.openingHours || null,
    phone: apiRestaurant.phone || null,

    // Recommendations
    isRecommended:
      apiRestaurant.isRecommended ||
      (apiRestaurant.statistics?.rating ?? apiRestaurant.rating ?? 0) >= 4.5,

    // Distance (if available)
    distance: apiRestaurant.distance || null,

    // Availability
    isVisible: apiRestaurant.isVisible !== false
  };
}

/**
 * Transform an array of restaurants from API format to component format
 * @param {Array|Object} apiRestaurants - Array of restaurant objects or response object with restaurants array
 * @returns {Array} - Array of transformed restaurant objects
 */
export function transformRestaurants(apiRestaurants) {
  let restaurants = [];

  console.log('[transformRestaurants] Input:', apiRestaurants);

  // Handle both direct array and response object formats
  if (Array.isArray(apiRestaurants)) {
    restaurants = apiRestaurants;
  } else if (typeof apiRestaurants === 'object' && apiRestaurants !== null) {
    // Extract restaurants array from response object
    if (Array.isArray(apiRestaurants.restaurants)) {
      restaurants = apiRestaurants.restaurants;
    } else if (Array.isArray(apiRestaurants?.data?.data)) {
      restaurants = apiRestaurants?.data?.data;
    } else if (Array.isArray(apiRestaurants.data)) {
      restaurants = apiRestaurants.data;
    } else if (Array.isArray(apiRestaurants.data?.restaurants)) {
      restaurants = apiRestaurants.data.restaurants;
    }
  }

  console.log(
    '[transformRestaurants] Extracted restaurants:',
    restaurants.length
  );

  const transformed = restaurants.map(transformRestaurant).filter((r) => r.id);

  console.log(
    '[transformRestaurants] Transformed and filtered:',
    transformed.length
  );

  return transformed;
}

/**
 * Normalize categories data to consistent format
 * @param {Array} categories - Categories from API (can be objects or strings)
 * @returns {Array} - Normalized categories array
 */
function normalizeCategories(categories) {
  if (!categories) {
    return [];
  }

  if (Array.isArray(categories)) {
    return categories.map((cat) => {
      if (typeof cat === 'string') {
        return { name: cat };
      }
      if (typeof cat === 'object' && cat.name) {
        return cat;
      }
      return { name: String(cat) };
    });
  }

  return [];
}

/**
 * Transform a single category object from API format to component format
 * @param {Object} apiCat - Category object from API
 * @returns {Object} - Transformed category object
 */
export function transformCategory(apiCat) {
  if (!apiCat || typeof apiCat !== 'object') {
    return {};
  }

  return {
    id: apiCat.id,
    name: apiCat.name,
    icon: apiCat.iconUrl || null,
    image: apiCat.imageUrl || null,
    active: apiCat.active !== false
    // color : pas de champ API, géré hardcodé côté composant
  };
}

/**
 * Transform an array of categories from API format to component format
 * @param {Array|Object} data - Array of category objects or response object
 * @returns {Array} - Array of transformed category objects
 */
export function transformCategories(data) {
  const arr = Array.isArray(data?.data)
    ? data.data
    : Array.isArray(data)
      ? data
      : [];
  return arr
    .filter((c) => c.id && c.name && c.active !== false)
    .map(transformCategory);
}

/**
 * Transform featured dishes response
 * Handles various response formats from the aggregation API
 * @param {Object|Array} responseData - Response data from API
 * @returns {Array} - Transformed dishes array
 */
export function transformDishes(responseData) {
  let dishes = [];

  // Extract dishes array from various response formats
  if (Array.isArray(responseData)) {
    dishes = responseData;
  } else if (typeof responseData === 'object' && responseData !== null) {
    if (Array.isArray(responseData.dishes)) {
      dishes = responseData.dishes;
    } else if (Array.isArray(responseData.data?.data)) {
      dishes = responseData.data.data;
    } else if (Array.isArray(responseData.data)) {
      dishes = responseData.data;
    }
  }

  // The aggregation API returns enriched dishes, so we can return them directly
  // or apply minor normalization if needed.
  return dishes.filter((d) => d && d.id);
}

/**
 * Transform a single ad object from API format to component format
 * @param {Object} apiAd - Ad object from API
 * @returns {Object} - Transformed ad object
 */
export function transformAd(apiAd) {
  if (!apiAd || typeof apiAd !== 'object') {
    return {};
  }

  // Extract metadata fields and bring them to root level
  const metadata = apiAd.metadata || {};

  return {
    // Core fields
    id: apiAd.id || apiAd._id,
    title: apiAd.title,
    description: apiAd.description,

    // Image and link - map from API format to component format
    // Nouvelle API : imageUrl et link (anciens : picture et url)
    picture:
      apiAd.picture || apiAd.imageUrl || apiAd.metadata?.imageUrlNotToUse || '',
    url: apiAd.url || apiAd.link || '',

    // Interactive features - prioritize root level fields, fallback to metadata/mock
    actionType: apiAd.actionType || metadata.actionType || null,
    internalPage: apiAd.internalPage || metadata.internalPage || null,
    dishId: apiAd.dishId || metadata.dishId || null,
    restaurantId: apiAd.restaurantId || metadata.restaurantId || null,
    feature: apiAd.feature || metadata.feature || null,
    coupon: apiAd.coupon || metadata.coupon || null,

    // Tout le reste dans metadata
    restaurantId: apiAd.restaurantId || apiAd.metadata?.restaurantId || null,
    dishId: apiAd.dishId || apiAd.metadata?.dishId || null,
    internalPage: apiAd.internalPage || apiAd.metadata?.internalPage || null,
    coupon: apiAd.coupon || apiAd.metadata?.coupon || null,
    order: apiAd.order || apiAd.metadata?.order || 0,

    // Active status and order
    active: apiAd.active !== false,
    order: apiAd.order || 0,

    // Position and targeting
    position: apiAd.position,
    targetAudience: apiAd.targetAudience,

    // Dates
    startDate: apiAd.startDate,
    endDate: apiAd.endDate,

    // Preserve other fields
    ...apiAd
  };
}

/**
 * Transform an array of ads from API format to component format
 * @param {Array} apiAds - Array of ad objects from API
 * @returns {Array} - Array of transformed ad objects
 */
export function transformAds(apiAds) {
  if (!Array.isArray(apiAds)) {
    return [];
  }

  return apiAds
    .map(transformAd)
    .sort((a, b) => (a.order || 0) - (b.order || 0));
}

/**
 * Transform ads response
 * Handles both { ads: [] }, { data: [] } and direct [] formats
 * @param {Object|Array} responseData - Response data from API
 * @returns {Array} - Transformed ads array
 */
export function transformAdsResponse(responseData) {
  let ads = [];

  // Extract ads array from various response formats
  if (Array.isArray(responseData)) {
    ads = responseData;
  } else if (typeof responseData === 'object' && responseData !== null) {
    if (Array.isArray(responseData.ads)) {
      ads = responseData.ads;
    } else if (Array.isArray(responseData.data)) {
      ads = responseData.data;
    }
  }

  return transformAds(ads);
}

/**
 * Transform a single deal object from API format to component format
 * @param {Object} apiDeal - Deal object from API
 * @returns {Object} - Transformed deal object
 */
export function transformDeal(apiDeal) {
  if (!apiDeal || typeof apiDeal !== 'object') {
    return {};
  }

  return {
    id: apiDeal.id,
    restaurantId: apiDeal.restaurantId,
    targetType: apiDeal.targetType,
    discountType: apiDeal.discountType,
    discountValue: apiDeal.discountValue,
    validFrom: apiDeal.validFrom,
    validUntil: apiDeal.validUntil,
    isActive: apiDeal.isActive !== false,

    // Aggregation API specific fields
    title: apiDeal.title || apiDeal.targetData?.title || apiDeal.name,
    description: apiDeal.description || apiDeal.targetData?.description,
    image:
      apiDeal.image ||
      apiDeal.targetData?.image ||
      apiDeal.targetData?.principalImageUrl,
    restaurantName:
      apiDeal.restaurantName || apiDeal.targetData?.restaurantName,
    restaurantLogo:
      apiDeal.restaurantLogo || apiDeal.targetData?.restaurantLogo,
    dishIds:
      apiDeal.dishIds ||
      apiDeal.targetData?.dishIds ||
      (apiDeal.targetData?.id ? [apiDeal.targetData.id] : []),
    targetData: apiDeal.targetData || {}
  };
}

/**
 * Transform deals response
 * @param {Array|Object} responseData - Response data from API
 * @returns {Array} - Array of transformed deal objects
 */
export function transformDealsResponse(responseData) {
  const arr = Array.isArray(responseData?.data)
    ? responseData.data
    : Array.isArray(responseData)
      ? responseData
      : [];
  return arr.map(transformDeal).filter((d) => d.id && d.isActive);
}

export default {
  transformRestaurant,
  transformRestaurants,
  transformDishes,
  transformAd,
  transformAds,
  transformAdsResponse,
  transformSponsorship,
  transformSponsorships,
  transformSponsorshipsResponse,
  transformCategory,
  transformCategories,
  transformDeal,
  transformDealsResponse,
  fixDuplicateFirstLetters
};

/**
 * Transform a single sponsorship object from API format to component format
 * @param {Object} apiSponsorship - Sponsorship object from API
 * @returns {Object} - Transformed sponsorship object
 */
export function transformSponsorship(apiSponsorship) {
  if (!apiSponsorship || typeof apiSponsorship !== 'object') {
    return {};
  }

  return {
    // Preserve all fields from API
    ...apiSponsorship,

    // Overwrite/Clean specific fields for component use
    id: apiSponsorship.id,
    restaurantId: apiSponsorship.restaurantId,

    // Banner image for carousel
    bannerImageUrl: apiSponsorship.bannerImageUrl || null,

    // Promotion details
    promotionDetails: apiSponsorship.promotionDetails || {},

    // Status
    isActive: apiSponsorship.status === 'active',

    // Date range
    startDate: apiSponsorship.startDate,
    endDate: apiSponsorship.endDate,

    // Amount
    amount: apiSponsorship.amount,
    paymentReference: apiSponsorship.paymentReference,

    // Has promotion flag
    hasPromotion: apiSponsorship.hasPromotion || false,

    // Nested restaurant info - transform if present
    restaurant: apiSponsorship.restaurant
      ? transformRestaurant(apiSponsorship.restaurant)
      : null
  };
}

/**
 * Transform an array of sponsorships from API format
 * @param {Array} apiSponsorships - Array of sponsorship objects from API
 * @returns {Array} - Array of transformed sponsorship objects
 */
export function transformSponsorships(apiSponsorships) {
  let sponsorships = [];

  // Handle both direct array and response object formats
  if (Array.isArray(apiSponsorships)) {
    sponsorships = apiSponsorships;
  } else if (typeof apiSponsorships === 'object' && apiSponsorships !== null) {
    if (Array.isArray(apiSponsorships.sponsorships)) {
      sponsorships = apiSponsorships.sponsorships;
    } else if (Array.isArray(apiSponsorships.data)) {
      sponsorships = apiSponsorships.data;
    }
  }

  return sponsorships
    .map(transformSponsorship)
    .filter(
      (s) =>
        s.id &&
        s.restaurantId &&
        s.status == 'paid' &&
        new Date(s.startDate) <= new Date() &&
        new Date(s.endDate) >= new Date()
    );
}

/**
 * Transform sponsorship response
 * Handles both { sponsorships: [] }, { data: [] } and direct [] formats
 * @param {Object|Array} responseData - Response data from API
 * @returns {Array} - Transformed sponsorships array
 */
export function transformSponsorshipsResponse(responseData) {
  let sponsorships = [];
  // Gère res.data = {data: {data: [...], total, limit, offset}}
  if (responseData?.data?.data && Array.isArray(responseData.data.data)) {
    sponsorships = responseData.data.data;
  } else if (Array.isArray(responseData?.data)) {
    sponsorships = responseData.data;
  } else if (Array.isArray(responseData)) {
    sponsorships = responseData;
  } else {
    sponsorships = [];
  }

  return transformSponsorships(sponsorships);
}
